import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ServiceBase } from './service.base';
import { AuthService, ResponseObject } from './auth.service';

/**
 * Enquiries sent to the artist by members of the public — from a public artwork page, or from the
 * contact form and work galleries on their published website.
 *
 * These are **not** `ProductRequestQueue` rows and deliberately so. That entity models a
 * gallery asking an artist for a piece: its `GalleryId` is a foreign key to `ApplicationUser`, and
 * a member of the public has no account for it to point at. It also has nowhere to put a sender's
 * name, email or message. The two are shown together on one screen; only the storage differs.
 */
@Injectable({
  providedIn: 'root'
})
export class ArtistProductEnquiryService extends ServiceBase {

  private api: string = '/ArtistProductEnquiry';

  constructor(private http: HttpClient,
    authService: AuthService) {
    super(authService);
  }

  /** The signed-in artist's own enquiries, newest first. Scoped server-side to the token. */
  public getMyEnquiries(): Observable<ResponseObject> {
    const url: string = environment.BASE_API_PATH + this.api + '/fetch';
    return this.http.get<ResponseObject>(url, this.RequestHeaders());
  }

  public markRead(enquiryId: string): Observable<ResponseObject> {
    const url: string = environment.BASE_API_PATH + this.api + '/' + enquiryId + '/mark-read';
    return this.http.post<ResponseObject>(url, {}, this.RequestHeaders());
  }
}

/**
 * One enquiry as the API returns it.
 *
 * `ArtistProductId` and the product fields are **null when the enquiry came from the website's
 * contact form** rather than from a specific piece — the same row shape covers both, which is why
 * the artist's inbox can hold them together.
 */
export class ArtistProductEnquiryDto {
  Id!: string;
  ArtistProductId!: string | null;
  ArtistProductName!: string | null;
  ProductImgUrl!: string | null;
  SenderName!: string;
  SenderEmail!: string;
  SenderPhone!: string | null;
  Message!: string;
  QuotedPrice!: number | null;
  /** Where it was sent from, e.g. `/profile/frances-smith`. */
  SourcePath!: string | null;
  CreatedDate!: string;
  ReadDate!: string | null;
  RespondedDate!: string | null;
}
